// =============================================================================
// SHANIZ GAMING ADMIN - RECONCILIATION
// =============================================================================

import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Skeleton,
  Snackbar,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  IconButton,
  Grid,
} from '@mui/material';
import {
  PlayCircleOutline as RunIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { reconciliationService, ReconciliationRun, ReconciliationMismatch } from '../services/api';
import { PermissionButton } from '../components/PermissionButton';

const RunStatusChip: React.FC<{ status: ReconciliationRun['status'] }> = ({ status }) => {
  const map: Record<ReconciliationRun['status'], 'info' | 'success' | 'error'> = {
    RUNNING: 'info',
    COMPLETED: 'success',
    FAILED: 'error',
  };
  return <Chip size="small" color={map[status]} label={status} />;
};

function mismatchesToCsv(mismatches: ReconciliationMismatch[]): string {
  const headers = ['id', 'runId', 'kind', 'localRef', 'remoteRef', 'localAmount', 'remoteAmount', 'localStatus', 'remoteStatus', 'userId', 'detectedAt', 'resolved', 'resolutionNote'];
  const rows = mismatches.map((m) =>
    headers
      .map((h) => {
        const v = (m as unknown as Record<string, unknown>)[h];
        if (v === null || v === undefined) return '';
        const s = String(v).replace(/"/g, '""');
        return /[",\n]/.test(s) ? `"${s}"` : s;
      })
      .join(',')
  );
  return [headers.join(','), ...rows].join('\n');
}

function downloadCsv(filename: string, csv: string) {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

const Reconciliation: React.FC = () => {
  const qc = useQueryClient();
  const [selectedRun, setSelectedRun] = useState<ReconciliationRun | null>(null);
  const [onlyUnresolved, setOnlyUnresolved] = useState(true);
  const [resolveOpen, setResolveOpen] = useState<ReconciliationMismatch | null>(null);
  const [resolveNote, setResolveNote] = useState('');
  const [snackbar, setSnackbar] = useState<{ open: boolean; msg: string; sev: 'success' | 'error' }>({ open: false, msg: '', sev: 'success' });

  const runs = useQuery({
    queryKey: ['recon', 'runs'],
    queryFn: () => reconciliationService.getRuns(1, 30),
    refetchInterval: (q) => {
      // Backend returns { runs: [...] }; older builds returned { items } / { data }.
      const d: any = q.state.data;
      const list = (d?.runs || d?.items || d?.data || []) as ReconciliationRun[];
      return list.some((r) => r.status === 'RUNNING') ? 5000 : false;
    },
  });

  const runList = useMemo<ReconciliationRun[]>(
    () => {
      const d: any = runs.data;
      return (d?.runs || d?.items || d?.data || []) as ReconciliationRun[];
    },
    [runs.data]
  );

  const mismatches = useQuery({
    queryKey: ['recon', 'mismatches', selectedRun?.id, onlyUnresolved],
    queryFn: () => reconciliationService.getMismatches(selectedRun!.id, onlyUnresolved),
    enabled: Boolean(selectedRun?.id),
  });

  const trigger = useMutation({
    mutationFn: () => reconciliationService.triggerRun('ALL'),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['recon', 'runs'] });
      setSnackbar({ open: true, msg: 'Reconciliation run started', sev: 'success' });
    },
    onError: (e: Error) => setSnackbar({ open: true, msg: e.message || 'Run failed to start', sev: 'error' }),
  });

  const resolve = useMutation({
    mutationFn: ({ id, note }: { id: string; note: string }) => reconciliationService.resolveMismatch(id, note),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['recon', 'mismatches'] });
      setResolveOpen(null);
      setResolveNote('');
      setSnackbar({ open: true, msg: 'Mismatch resolved', sev: 'success' });
    },
    onError: (e: Error) => setSnackbar({ open: true, msg: e.message || 'Resolve failed', sev: 'error' }),
  });

  const handleDownload = () => {
    if (!mismatches.data) return;
    const unresolved = mismatches.data.filter((m) => !m.resolved);
    const csv = mismatchesToCsv(unresolved);
    downloadCsv(`reconciliation-${selectedRun?.id || 'run'}-unresolved.csv`, csv);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2, gap: 2 }}>
        <Box>
          <Typography variant="h4" fontWeight={700}>
            Reconciliation
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, maxWidth: 720 }}>
            Cross-checks every payment-gateway transaction against our internal ledger to catch drift:
            duplicates, missing records, amount mismatches. A daily cron job runs at 02:00 Africa/Nairobi
            — use <b>Run Now</b> to kick one off manually. Each run lists the mismatches it found so you
            can investigate and mark them resolved.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
          <Tooltip title="Refresh">
            <IconButton onClick={() => runs.refetch()}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <PermissionButton
            permission="reconciliation.run"
            variant="outlined"
            color="inherit"
            startIcon={<RunIcon />}
            disabled={trigger.isPending}
            onClick={() => trigger.mutate()}
            sx={{ borderColor: 'divider', color: 'text.primary', '&:hover': { borderColor: 'text.primary', bgcolor: 'action.hover' } }}
          >
            {trigger.isPending ? 'Running…' : 'Run Now'}
          </PermissionButton>
        </Stack>
      </Box>

      <Alert severity="info" icon={false} sx={{ mb: 3, bgcolor: 'action.hover', border: 1, borderColor: 'divider' }}>
        <Typography variant="body2">
          <strong>What a run does:</strong> fetches the last 24 h of transactions from the payment provider
          (M-Pesa / Vooma / gateway) and diffs them against <code>wallet_transactions</code>. Differences are
          saved as <em>mismatches</em> with a reason (duplicate provider entry, missing local record,
          amount drift). Unresolved mismatches block end-of-day settlement.
        </Typography>
      </Alert>

      <Grid container spacing={3}>
        <Grid item xs={12} md={5}>
          <Card>
            <CardContent>
              <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                Recent Runs
              </Typography>
              {runs.isError && <Alert severity="error">Failed to load runs.</Alert>}
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Started</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right">Mismatches</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {runs.isLoading && Array.from({ length: 5 }).map((_, i) => (
                      <TableRow key={i}>
                        <TableCell colSpan={3}>
                          <Skeleton />
                        </TableCell>
                      </TableRow>
                    ))}
                    {!runs.isLoading && runList.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={3}>
                          <Typography color="text.secondary" textAlign="center" py={3}>
                            No runs yet. Click "Run Now" to start one.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                    {runList.map((r) => (
                      <TableRow
                        key={r.id}
                        hover
                        selected={selectedRun?.id === r.id}
                        onClick={() => setSelectedRun(r)}
                        sx={{ cursor: 'pointer' }}
                      >
                        <TableCell>
                          <Typography variant="body2">{r.startedAt ? dayjs(r.startedAt).format('MMM D HH:mm') : '—'}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            by {r.triggeredBy || 'scheduler'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <RunStatusChip status={r.status} />
                        </TableCell>
                        <TableCell align="right">
                          <Typography fontWeight={700} color={r.mismatchCount ? 'error.main' : 'success.main'}>
                            {r.mismatchCount}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            of {r.totalChecked}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={7}>
          <Card>
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="subtitle1" fontWeight={700}>
                  {selectedRun ? `Mismatches for run ${selectedRun.id.slice(0, 8)}` : 'Select a run to see mismatches'}
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                  <FormControlLabel
                    control={<Switch checked={onlyUnresolved} onChange={(e) => setOnlyUnresolved(e.target.checked)} />}
                    label="Unresolved only"
                  />
                  <Button
                    variant="outlined"
                    startIcon={<DownloadIcon />}
                    disabled={!mismatches.data?.length}
                    onClick={handleDownload}
                  >
                    CSV
                  </Button>
                </Stack>
              </Stack>

              {!selectedRun && (
                <Alert severity="info">Choose a run on the left to view its mismatches.</Alert>
              )}
              {selectedRun && mismatches.isLoading && <Skeleton variant="rectangular" height={240} />}
              {selectedRun && mismatches.isError && <Alert severity="error">Failed to load mismatches.</Alert>}
              {selectedRun && mismatches.data && mismatches.data.length === 0 && (
                <Alert severity="success">No mismatches for this run. Books balance.</Alert>
              )}
              {selectedRun && mismatches.data && mismatches.data.length > 0 && (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Kind</TableCell>
                        <TableCell>Local</TableCell>
                        <TableCell>Remote</TableCell>
                        <TableCell>Detected</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell align="right" />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {mismatches.data.map((m) => (
                        <TableRow key={m.id} hover>
                          <TableCell>
                            <Chip size="small" label={m.kind} />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                              {m.localRef || '—'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {m.localStatus} · {m.localAmount?.toLocaleString?.() ?? '—'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                              {m.remoteRef || '—'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {m.remoteStatus} · {m.remoteAmount?.toLocaleString?.() ?? '—'}
                            </Typography>
                          </TableCell>
                          <TableCell>{dayjs(m.detectedAt).format('MMM D HH:mm')}</TableCell>
                          <TableCell>
                            {m.resolved ? (
                              <Chip size="small" color="success" label="Resolved" />
                            ) : (
                              <Chip size="small" color="warning" label="Unresolved" />
                            )}
                          </TableCell>
                          <TableCell align="right">
                            {!m.resolved && (
                              <PermissionButton
                                permission="reconciliation.resolve"
                                size="small"
                                variant="outlined"
                                onClick={() => setResolveOpen(m)}
                              >
                                Resolve
                              </PermissionButton>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Dialog open={Boolean(resolveOpen)} onClose={() => setResolveOpen(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Resolve mismatch</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Document how the discrepancy was resolved. This is recorded in the audit log.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            multiline
            rows={4}
            label="Resolution note"
            value={resolveNote}
            onChange={(e) => setResolveNote(e.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResolveOpen(null)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!resolveNote.trim() || resolve.isPending || !resolveOpen}
            onClick={() => resolveOpen && resolve.mutate({ id: resolveOpen.id, note: resolveNote.trim() })}
          >
            Resolve
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.sev} variant="filled" onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Reconciliation;
